import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRightIcon } from 'lucide-react';
import { authors } from '../data/authors';
import { articles } from '../data/articles';
import { usePageMeta } from '../hooks/usePageMeta';

/**
 * Every writer behind the guides, with what they've written. Each author has
 * an anchor (#author-id) so bylines can link straight to their entry.
 */
export function AuthorsPage() {
  usePageMeta('Our Authors | FindOnlineColleges', 'Meet the editors and writers behind the FindOnlineColleges guides — former advisors, nurses, and working professionals who earned their own degrees online.');
  const location = useLocation();

  // Honor #author-id links from article bylines once the page has rendered.
  useEffect(() => {
    const id = location.hash.replace('#', '');
    if (!id) {
      window.scrollTo(0, 0);
      return;
    }
    requestAnimationFrame(() => {
      document.getElementById(id)?.scrollIntoView({
        block: 'start'
      });
    });
  }, [location.hash]);
  return <main className="bg-white" data-id="element-1120" data-mp="2x7ewcnaz86dj">
      <header className="hero-bg" data-id="element-1121" data-mp="2s7accnav0tik">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16" data-id="element-1122" data-mp="2s5zcgykjc5wq">
          <nav aria-label="Breadcrumb" className="mb-6" data-id="element-1123" data-mp="2n75uasuazqq8">
            <ol className="flex flex-wrap items-center gap-2 text-xs text-white/70" data-id="element-1124" data-mp="2na0hgs4pbpnc">
              <li data-id="element-1125" data-mp="2j85e7i37n26x">
                <Link to="/" className="hover:text-white transition-colors" data-id="element-1126" data-mp="2j8oyh5gc22o4">
                  Home
                </Link>
              </li>
              <li aria-hidden="true" data-id="element-1127" data-mp="3ax8md8y7wwou">/</li>
              <li className="text-white/90" data-id="element-1128" data-mp="3azlay4yhe5av">Our Authors</li>
            </ol>
          </nav>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-4" data-id="element-1129" data-mp="364yzy2wbaerb">
            The People Behind Our Guides
          </h1>
          <p className="text-lg text-white/80 leading-relaxed max-w-2xl" data-id="element-1130" data-mp="2hyl2325jvi4">
            Our writers have sat where you're sitting — advising adult
            learners, working full time, and finishing degrees online. Every
            guide is written and reviewed by someone who knows the path
            firsthand.
          </p>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16" data-id="element-1131" data-mp="1wzrn08rtn1aa">
        <ul className="divide-y divide-gray-200" data-id="element-1132" data-mp="1i8r3n8pjdmzd">
          {authors.map(author => {
          const written = articles.filter(a => a.authorId === author.id);
          return <li key={author.id} id={author.id} className="py-10 first:pt-0 last:pb-0 scroll-mt-24 grid gap-6 sm:grid-cols-[8rem_1fr] sm:gap-8" data-id="element-1133" data-mp="196rlue6p6evd">
                <img src={author.image} alt={author.name} loading="lazy" className="w-24 h-24 sm:w-32 sm:h-32 rounded-full object-cover" data-id="element-1134" data-mp="zbzlx3pn7fxf" />
                <div className="min-w-0" data-id="element-1135" data-mp="2ynaidriqjkwf">
                  <h2 className="text-2xl font-bold text-navy leading-tight" data-id="element-1136" data-mp="2ym7u7rcsrwk7">
                    {author.name}
                  </h2>
                  <p className="text-sm font-medium text-gray-500 mt-1 mb-4" data-id="element-1137" data-mp="mxum2erjt1xd">
                    {author.role}
                  </p>
                  <p className="text-base text-gray-700 leading-relaxed max-w-2xl" data-id="element-1138" data-mp="wpgvc5kbsqxh">
                    {author.bio}
                  </p>

                  {written.length > 0 && <div className="mt-6" data-id="element-1139" data-mp="15qezcnurkyg2">
                      <h3 className="text-sm font-semibold text-gray-900 mb-3" data-id="element-1140" data-mp="15pdk076ptpsj">
                        Guides by {author.name.split(',')[0].split(' ')[0]}
                      </h3>
                      <ul className="space-y-2" data-id="element-1141" data-mp="1fouqgqtsk7pk">
                        {written.map(article => <li key={article.slug} data-id="element-1142" data-mp="1ap8eekpp8xic">
                            <Link to={`/article/${article.slug}`} className="group inline-flex items-start gap-2 text-sm text-navy hover:text-navy-dark font-medium" data-id="element-1143" data-mp="1anf8x8qtojbx">
                              <ArrowRightIcon className="w-4 h-4 mt-0.5 flex-shrink-0 transition-transform duration-200 group-hover:translate-x-0.5" aria-hidden="true" data-id="element-1144" data-mp="3dx0eb573lxz6" />
                              <span className="group-hover:underline" data-id="element-1145" data-mp="3sv13zhhdvdkv">
                                {article.title}
                              </span>
                            </Link>
                          </li>)}
                      </ul>
                    </div>}
                </div>
              </li>;
        })}
        </ul>
      </div>
    </main>;
}